/**
 * Zoom state for a document viewer, with "Fit" as a MODE rather than a value.
 *
 * Before 2026-09-23 Fit was applied once per document load (a `fitAppliedRef`
 * latch) and from then on the zoom was just a number. So closing the Teams side
 * panel -- which widens the meeting stage -- recomputed the fit value but left
 * the document at the old scale, and the user had to pick another zoom and then
 * Fit again to get it back. Now, while the user is in fit mode, every new fit
 * value the viewer reports is applied; picking any explicit zoom leaves the mode.
 */
export interface FitZoomState {
  /** The zoom actually handed to the viewer. */
  zoom: number;
  /** Latest fit-to-width zoom the viewer reported, if it reports one at all. */
  fitZoom: number | undefined;
  /** True until the user picks an explicit zoom; re-entered by choosing Fit. */
  fitMode: boolean;
}

export type FitZoomAction =
  /** The viewer measured its container and worked out the fit zoom. */
  | { type: 'fitReported'; fitZoom: number }
  /** The user chose an explicit zoom (preset, +/-). */
  | { type: 'zoomChosen'; zoom: number }
  /** The user chose Fit. */
  | { type: 'fitChosen' }
  /** A document was opened or closed: back to the initial state. */
  | { type: 'reset' };

export const INITIAL_FIT_ZOOM: FitZoomState = { zoom: 1, fitZoom: undefined, fitMode: true };

export function fitZoomReducer(state: FitZoomState, action: FitZoomAction): FitZoomState {
  switch (action.type) {
    case 'fitReported': {
      if (!(action.fitZoom > 0)) return state;
      const zoom = state.fitMode ? action.fitZoom : state.zoom;
      if (action.fitZoom === state.fitZoom && zoom === state.zoom) return state;
      return { ...state, fitZoom: action.fitZoom, zoom };
    }
    case 'zoomChosen':
      if (!state.fitMode && action.zoom === state.zoom) return state;
      return { ...state, zoom: action.zoom, fitMode: false };
    case 'fitChosen':
      if (state.fitZoom == null) return state;
      return { ...state, zoom: state.fitZoom, fitMode: true };
    case 'reset':
      return INITIAL_FIT_ZOOM;
  }
}

/**
 * Fit-to-width zoom for an image, in the units ImageViewer's zoom uses.
 *
 * An image at zoom 1 is already drawn at min(container, natural) width -- it is
 * never enlarged -- so a large image fits at exactly 1, and only an image
 * narrower than its container needs more. Capped at 2 like PdfViewer, so a tiny
 * image does not blow up into a blur.
 */
export function imageFitZoom(naturalWidth: number, containerWidth: number): number | undefined {
  if (naturalWidth <= 0 || containerWidth <= 0) return undefined;
  return Math.min(2, Math.max(1, containerWidth / naturalWidth));
}
